import { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { FilterQueryType } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import StatusFilter from "./StatusFilter";
import ParentFilterChip from "./ParentFilterChip";
import AccountFilter from "./AccountFilter";
import AmountFilter from "./AmountFilter";
import DateFilter from "./DateFilter";
import FrequencyFilter from "./FrequencyFilter";
import StaticFilter from "./StaticFilter";
import BulkStatusFilter from "./BulkStatusFilter";
import TemplateStatusFilter from "./TemplateStatusFilter";
import MethodFilter from "./MethodFilter";
import FileImportMethodFilter from "./FileImportMethodFilter";
import ActionFilter from "./ActionFilter";
import TemplateFilter from "./TemplateFilter";
import { FilterChipsType, FilterChips, FILTER_CONFIG } from "./filterConfig";
import styles from "./Filters.module.scss";

// Used by both Payment Activity and Bulk Templates
export const FilterContainer = ({
  setFilters,
  filters,
  allowedFilters,
  parentSelectableFilters,
}: {
  setFilters: (filters: FilterQueryType) => void;
  filters: FilterQueryType;
  allowedFilters: FilterChipsType[];
  parentSelectableFilters: FilterChipsType[];
}) => {
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState<FilterChips | "">("");
  const [visibleFilters, setVisibleFilters] = useState<FilterChipsType[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    setActiveFilter("");
  }, [location.pathname]);

  // used to compute chips from URL/query filters
  useEffect(() => {
    const initialVisibleFilters: FilterChipsType[] = [];
    (
      Object.entries(FILTER_CONFIG) as [
        FilterChipsType,
        (typeof FILTER_CONFIG)[FilterChipsType]
      ][]
    ).forEach(([filterName, config]) => {
      if (config.type === "multi-select" || config.type === "static") {
        const value = filters[config.queryKey];
        if (Array.isArray(value) && value.length > 0) {
          initialVisibleFilters.push(filterName);
        }
      }

      if (config.type === "range") {
        const minVal = filters[config.queryKeys.min];
        const maxVal = filters[config.queryKeys.max];
        if (minVal !== undefined || maxVal !== undefined) {
          initialVisibleFilters.push(filterName);
        }
      }
    });

    // also show the TEMPLATE chip when navigated via ?template=<group_uuid>
    if (filters.template) {
      initialVisibleFilters.push(FilterChips.TEMPLATE as FilterChipsType);
    }

    setVisibleFilters(initialVisibleFilters);
  }, [filters]);

  const handleSetFilter = (newFilter: FilterQueryType) => {
    const updatedFilter = {
      ...filters,
      ...newFilter,
    };
    setFilters(updatedFilter);

    // when the page was loaded with URL query params, keep the URL in sync as
    // filters are applied.
    // when there are no initial query params, skip syncing to keep the URL clean.
    if (searchParams.toString().length === 0) {
      return;
    }

    // sync applied filter values to URL so they survive other filter removals
    (
      Object.entries(FILTER_CONFIG) as [
        FilterChipsType,
        (typeof FILTER_CONFIG)[FilterChipsType]
      ][]
    ).forEach(([, config]) => {
      if (config.type === "multi-select" || config.type === "static") {
        // template_version is not a URL param — only ?template= is supported
        if (config.queryKey === "template_version") return;
        const value = updatedFilter[config.queryKey];
        if (Array.isArray(value) && value.length > 0) {
          searchParams.set(config.queryKey, value.join(","));
        } else {
          searchParams.delete(config.queryKey);
        }
      } else if (config.type === "range") {
        const { min, max } = config.queryKeys;
        const minVal = updatedFilter[min];
        const maxVal = updatedFilter[max];
        if (minVal) {
          searchParams.set(min, String(minVal));
        } else {
          searchParams.delete(min);
        }
        if (maxVal) {
          searchParams.set(max, String(maxVal));
        } else {
          searchParams.delete(max);
        }
      }
    });

    // only remove ?template from URL when the filter is fully cleared,
    // not when it's being converted to template_version on apply
    if (!updatedFilter.template && !updatedFilter.template_version?.length) {
      searchParams.delete("template");
    }

    setSearchParams(searchParams, { replace: true });
  };

  // user clicks a chip (show/hide filter & clear values if closing)
  const handleToggleFilter = (filterName: FilterChipsType) => {
    const config = FILTER_CONFIG[filterName];
    const isVisible = visibleFilters.includes(filterName);

    if (isVisible) {
      // clear filter values if the chip is already visible
      if (config.type === "multi-select") {
        // also clear filters.template when removing the TEMPLATE chip, since
        // it may have been set via ?template=<group_uuid> without template_version
        const hasTemplateGroup =
          filterName === FilterChips.TEMPLATE && filters.template;
        if (filters[config.queryKey]?.length !== 0 || hasTemplateGroup) {
          setFilters({
            ...filters,
            [config.queryKey]: [],
            ...(filterName === FilterChips.TEMPLATE && { template: undefined }),
          });
        }
        searchParams.delete(config.queryKey);
        // clean up the ?template= URL param when removing the TEMPLATE chip
        if (filterName === FilterChips.TEMPLATE) {
          searchParams.delete("template");
        }
      }

      if (config.type === "static") {
        if (filters[config.queryKey] !== undefined) {
          setFilters({
            ...filters,
            [config.queryKey]: undefined,
          });
        }
        searchParams.delete(config.queryKey);
      }

      if (config.type === "range") {
        const { min, max } = config.queryKeys;
        const newFilters = { ...filters };

        if (filters[min] !== undefined) newFilters[min] = undefined;
        if (filters[max] !== undefined) newFilters[max] = undefined;

        if (filters[min] !== undefined || filters[max] !== undefined) {
          setFilters(newFilters);
        }

        searchParams.delete(min);
        searchParams.delete(max);
      }

      // remove from visibleFilters because we are closing it
      setVisibleFilters((prev) => prev.filter((f) => f !== filterName));

      // reset active filter state
      setActiveFilter("");
    } else {
      // show the filter chip if it’s not visible
      setVisibleFilters((prev) => [...prev, filterName]);

      // make it active so the filter UI opens
      setActiveFilter(filterName);
    }

    // update the URL query params
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div className={styles.filterContainer}>
      {/* method and date filter are special filters that are just always visible
      and chilling next to the parent filter chip */}
      {allowedFilters.includes(FilterChips.METHOD) ? (
        <MethodFilter
          isActive={activeFilter === FilterChips.METHOD}
          filters={filters}
          handleSetFilter={handleSetFilter}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
        />
      ) : null}
      {allowedFilters.includes(FilterChips.FILE_IMPORT_METHOD) ? (
        <FileImportMethodFilter
          isActive={activeFilter === FilterChips.FILE_IMPORT_METHOD}
          filters={filters}
          handleSetFilter={handleSetFilter}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
        />
      ) : null}
      {allowedFilters.includes(FilterChips.DATE) ? (
        <DateFilter
          isActive={activeFilter === FilterChips.DATE}
          filters={filters}
          handleSetFilter={handleSetFilter}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
        />
      ) : null}

      {/* the below filters are normal filters that you can only access in parent filter chip */}
      {visibleFilters.includes(FilterChips.STATUS) &&
      allowedFilters.includes(FilterChips.STATUS) ? (
        <StatusFilter
          isActive={activeFilter === FilterChips.STATUS}
          filters={filters}
          handleSetFilter={handleSetFilter}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
        />
      ) : null}
      {visibleFilters.includes(FilterChips.BULK_STATUS) &&
      allowedFilters.includes(FilterChips.BULK_STATUS) ? (
        <BulkStatusFilter
          isActive={activeFilter === FilterChips.BULK_STATUS}
          filters={filters}
          handleSetFilter={handleSetFilter}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
        />
      ) : null}
      {visibleFilters.includes(FilterChips.TEMPLATE_STATUS) &&
      allowedFilters.includes(FilterChips.TEMPLATE_STATUS) ? (
        <TemplateStatusFilter
          isActive={activeFilter === FilterChips.TEMPLATE_STATUS}
          filters={filters}
          handleSetFilter={handleSetFilter}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
        />
      ) : null}
      {visibleFilters.includes(FilterChips.AMOUNT) &&
      allowedFilters.includes(FilterChips.AMOUNT) ? (
        <AmountFilter
          isActive={activeFilter === FilterChips.AMOUNT}
          filters={filters}
          handleSetFilter={handleSetFilter}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
        />
      ) : null}
      {visibleFilters.includes(FilterChips.ACCOUNT) &&
      allowedFilters.includes(FilterChips.ACCOUNT) ? (
        <AccountFilter
          isActive={activeFilter === FilterChips.ACCOUNT}
          filters={filters}
          handleSetFilter={handleSetFilter}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
        />
      ) : null}
      {visibleFilters.includes(FilterChips.FREQUENCY) &&
      allowedFilters.includes(FilterChips.FREQUENCY) ? (
        <FrequencyFilter
          isActive={activeFilter === FilterChips.FREQUENCY}
          filters={filters}
          handleSetFilter={handleSetFilter}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
        />
      ) : null}
      {visibleFilters.includes(FilterChips.ACTION) &&
      allowedFilters.includes(FilterChips.ACTION) ? (
        <ActionFilter
          isActive={activeFilter === FilterChips.ACTION}
          filters={filters}
          handleSetFilter={handleSetFilter}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
        />
      ) : null}
      {visibleFilters.includes(FilterChips.TEMPLATE) &&
      allowedFilters.includes(FilterChips.TEMPLATE) ? (
        <TemplateFilter
          isActive={activeFilter === FilterChips.TEMPLATE}
          filters={filters}
          handleSetFilter={handleSetFilter}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
        />
      ) : null}

      {/* recurring payment, bulk payment, and bulk reversal are "special" static filters where a user cannot select them */}
      {/* aka they do not appear in the parent filter dropdown */}
      {visibleFilters.includes(FilterChips.RECURRING_PAYMENT) ? (
        <StaticFilter
          isActive={activeFilter === FilterChips.RECURRING_PAYMENT}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
          type={FilterChips.RECURRING_PAYMENT}
        />
      ) : null}
      {visibleFilters.includes(FilterChips.BULK_PAYMENT) ? (
        <StaticFilter
          isActive={activeFilter === FilterChips.BULK_PAYMENT}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
          type={FilterChips.BULK_PAYMENT}
        />
      ) : null}
      {visibleFilters.includes(FilterChips.BULK_REVERSAL) ? (
        <StaticFilter
          isActive={activeFilter === FilterChips.BULK_REVERSAL}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
          type={FilterChips.BULK_REVERSAL}
        />
      ) : null}
      {visibleFilters.includes(FilterChips.WIRE_IMPORT_BATCH) ? (
        <StaticFilter
          isActive={activeFilter === FilterChips.WIRE_IMPORT_BATCH}
          handleSetActive={setActiveFilter}
          handleToggleFilter={handleToggleFilter}
          type={FilterChips.WIRE_IMPORT_BATCH}
        />
      ) : null}
      <ParentFilterChip
        visibleFilters={visibleFilters}
        isActive={activeFilter === FilterChips.PARENT}
        handleSetActive={setActiveFilter}
        handleToggleFilter={handleToggleFilter}
        parentSelectableFilters={parentSelectableFilters}
      />
    </div>
  );
};

export default FilterContainer;
